import { createContext, useCallback, useContext, useState } from 'react';
const CartContext = createContext([]);

/**
 *
 * @param {Array<Item>} cart
 * @param {Item} item
 * @returns {number}
 */
function findItemIndex(cart, item) {
    return cart.findIndex(x =>
        x.id === parseInt(item.id) &&
        ((x.size && x.size === item.size) || !x.size) &&
        ((x.option && x.option === item.option) || !x.option)
    )
}

/**
 * @typedef {{
 *   id: number
 *   amount: number
 * }} Item
 *
 *
 * @typedef {{
 * items: Item[]
 * getAmount(item: Item): number
 * setAmount(item: Item, amount: number | (number) => number)
 * getTotalAmount(): number
 * getTotal(itemPrice?: (item: Item) => number): number
 * add(item: Item): void
 * delete(item: Item): void
 * set(items: Array<Item>): void
 * clear(): void
 * hasShipping(): boolean
 * getShippingCost(): number
 * }} Cart
 */

/**
 * @returns {Cart}
 */
export default function useCart() {
    /**
     * @type {[Array<Item>, (items: Array<Item>) => void]}
     */
    const [cart, setCart] = useContext(CartContext)

    const set = useCallback((items) => {
        setCart([...items])
    }, [setCart])

    const clear = useCallback(() => {
        setCart([])
    }, [setCart])

    const add = useCallback((item) => {
        const itemIndex = findItemIndex(cart, item)

        if (itemIndex === -1) {
            set([...cart, item])
        } else {
            if (item.subType1 === 'Support') {
                cart[itemIndex].amount = 1
            } else if (item.type === 'Tickets') {
                cart[itemIndex].amount += item.amount;
                cart[itemIndex].amountCamper += item.amountCamper;
                cart[itemIndex].amountTent += item.amountTent;
            } else {
                cart[itemIndex].amount += item.amount;
            }

            set(cart)
        }
    }, [cart, set])

    const deleteItem = useCallback((item) => {
        const itemIndex = findItemIndex(cart, item)
        if (itemIndex === -1) {
            return
        }

        set([...cart.slice(0, itemIndex), ...cart.slice(itemIndex + 1)])
    }, [cart, set])

    const getAmount = useCallback((item) => {
        const itemIndex = findItemIndex(cart, item)

        if (itemIndex === -1) {
            return 0
        } else {
            return cart[itemIndex].amount
        }
    }, [cart])

    const setAmount = useCallback((item, amount) => {
        const itemIndex = findItemIndex(cart, item)

        if (itemIndex !== -1) {
            if (typeof amount === "function") {
                cart[itemIndex].amount = amount(cart[itemIndex].amount)
            } else {
                cart[itemIndex].amount = amount
            }

            if (Number.isNaN(cart[itemIndex].amount) || cart[itemIndex].amount <= 0) {
                deleteItem(item)
            } else {
                set(cart)
            }
        }
    }, [cart, deleteItem, set])

    const getTotalAmount = useCallback(() => {
        return cart.reduce((acc, item) => acc + item.amount, 0)
    }, [cart])

    const getTotal = useCallback((itemPrice) => {
        const price = itemPrice ?? (item => item.preis);
        return cart.reduce((acc, item) => acc + item.amount * price(item), 0)
    }, [cart])

    const hasShipping = useCallback(() => {
        return cart.some(item => item.versandkosten && item.versandkosten > 0)
    }, [cart])

    const getShippingCost = useCallback(() => {
        if (window.sessionStorage.getItem("abholung") === "true") {
            return 0;
        }
        return cart.reduce((acc, item) => {
            if (item.versandkosten && item.versandkosten > 0) {
                return Math.max(acc, item.versandkosten);
            } else {
                return acc;
            }
        }, 0)
    }, [cart])

    return {
        items: cart,
        hasShipping,
        getShippingCost,
        getAmount,
        setAmount,
        getTotalAmount,
        getTotal,
        add,
        delete: deleteItem,
        set,
        clear,
    }
}

export function CartProvider(props) {
    const KEY = "CART"
    const defaultState = JSON.parse(sessionStorage.getItem(KEY) ?? "[]")

    const [cart, setCartState] = useState(defaultState)

    const setCart = useCallback((value) => {
        if (!value) {
            value = []
        }

        sessionStorage.setItem(KEY, JSON.stringify(value))
        setCartState(value)
    }, [setCartState])

    return <CartContext.Provider value={[cart, setCart]} {...props} />
}